<script lang="ts" setup>
import AGrowthHack from '~/components/reusables/AGrowthHack.vue'
import AppStore from '~/components/reusables/AppStore.vue'
import GooglePlay from '~/components/reusables/GooglePlay.vue'
import YourWebHomepage from '~/components/reusables/YourWebHomepage.vue'

const { t } = useI18n()

const {
  public: { siteUrl },
} = useRuntimeConfig()

useSeoMeta({
  title: t('belong-pass-for-founders'),
  description: t('get-access-to-a-community-of'),
  ogTitle: t('belong-pass-for-founders'),
  ogDescription: t('get-access-to-a-community-of'),
  ogImage: `${siteUrl}/images/head/snippet_screen.png`,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogImageType: 'image/png',
  ogType: 'website',
  twitterTitle: t('belong-pass-for-founders'),
  twitterDescription: t('get-access-to-a-community-of'),
  twitterImage: `${siteUrl}/images/head/snippet_screen.png`,
  twitterCard: 'summary_large_image',
})

definePageMeta({
  layout: 'menu',
})
</script>

<template>
  <div class="body text-white">
    <section class="relative">
      <div class="h-174 lt-xl:h-auto">
        <div class="ml-26 lt-xl:mx-auto">
          <h1 class="pt-51 text-[#CB99FF] lt-xl:pt-159 lt-xl:text-center">
            {{ t('belong-founder-pass') }}
          </h1>
          <h1
            class="text-[1.75rem] font-400 tracking-wider mt-2.5 leading-[2.125rem] max-w-132.25 mb-0 lt-xl:text-center lt-xl:mx-auto lt-md:px-5.5 lt-md:text-center"
          >
            {{ t('get-access-to-a-community') }}
          </h1>
          <div class="flex flex-row mt-20.5 lt-xl:flex-col">
            <div
              class="text-[0.8125rem] tracking-wide leading-5 z-2 relative rounded-[1.875rem] bg-[#1B1B1B] w-61.75 h-8.75 flex items-center justify-center lt-xl:mx-auto"
            >
              {{ t('supply-remaining') }}
              <span class="text-[#CB99FF] ml-2.5">1000 / 1000</span>
            </div>
            <NuxtLink
              to="/"
              class="mt-1 text-[0.8125rem] font-300 leading-5 tracking-wide flex items-center ml-6 underline text-white lt-xl:justify-center lt-xl:z-2 lt-xl:mt-2 lt-xl:ml-0"
            >
              {{ t('how-can-i-get-one') }}
            </NuxtLink>
          </div>

          <div class="hidden xl:flex flex-row mt-8 gap-3.5">
            <AppStore />
            <GooglePlay />
          </div>
        </div>
        <div
          class="header-shadow w-full object-cover absolute top-0 -z-1 h-175"
        >
          <div
            class="video-desktop w-full object-cover absolute top-0 -z-1 lt-xl:h-175 xl:hidden"
          >
            <video
              autoplay
              loop
              muted
              playsinline
              class="w-full object-cover lt-xl:h-175"
            >
              <source src="/images/nft/video-mobile.mp4" type="video/mp4" />
            </video>
          </div>
          <div
            class="w-full h-full object-cover absolute top-0 -z-1 lt-sm:hidden lt-xl:hidden"
          >
            <video
              autoplay
              loop
              muted
              playsinline
              class="w-full h-full object-cover"
            >
              <source src="/images/nft/video-desktop-2.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </section>
    <section>
      <div
        class="founder-block flex flex-row justify-center lt-xl:flex-col-reverse lt-xl:items-center lt-xl:mb-7.5 lt-xl:mx-5.5"
      >
        <img
          :alt="t('early-access')"
          class="w-80.5 h-136.5 mt-19.5 z-3 object-cover relative lt-xl:mt-0 lt-xl:h-68.25 lt-xl:object-top"
          src="/images/founder/phone-1.webp"
        />
        <div
          class="mt-52.5 mb-34.5 ml-43.5 relative z-3 lt-xl:mx-auto lt-xl:mt-17.5 lt-xl:mb-11"
        >
          <div
            class="text-[1.75rem] leading-[2.625rem] text-[#FFB0EE] tracking-wider max-w-135.5 flex flex-row items-center lt-xl:justify-center lt-xl:text-center lt-sm:text-2xl"
          >
            {{ t('early-access') }}
          </div>
          <div
            class="text-lg leading-[1.75rem] tracking-wide max-w-127 mt-9.5 text-[#CECECE] lt-xl:ml-0 lt-xl:text-center lt-sm:text-lg"
          >
            {{ t('as-a-founder-nft-holder') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-[#272727]" />
    </section>
    <section>
      <div
        class="flex flex-row justify-center lt-xl:flex-col-reverse lt-xl:items-center"
      >
        <div class="mt-44.5 mr-27.5 mb-31 lt-xl:mx-5.5 lt-xl:mb-16 lt-xl:mt-0">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide text-[#7540EE] lt-xl:text-center lt-sm:text-2xl"
          >
            <p>
              {{ t('wl-spots') }}
              <span class="text-white">{{ t('and-exclusive-content') }}</span>
            </p>
          </div>
          <div
            class="max-w-130 mt-11.5 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] lt-xl:text-center lt-sm:text-lg"
          >
            {{ t('our-nft-holders') }}
          </div>
        </div>
        <img
          :alt="t('wl-spots') + t('and-exclusive-content')"
          class="wl-sports w-87 h-86.5 mt-39.5 mb-23.5 object-cover lt-xl:mt-4 lt-xl:mb-12.5"
          src="/images/founder/wl-sports.webp"
        />
      </div>
      <div class="w-full h-0.25 bg-[#272727]" />
    </section>

    <section>
      <div class="founder flex justify-center lt-xl:flex-col lt-xl:items-center">
        <img
          :alt="t('collection') + t('notifications')"
          class="collection-notifications w-92.5 h-110.75 object-cover my-19.5 lt-xl:mt-4 lt-xl:mb-0"
          src="/images/founder/premium-notifications.webp"
        />
        <div class="my-44.5 mb-51.5 ml-55 lt-xl:mx-5.5 lt-xl:mb-10.5 lt-xl:mt-4">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide lt-xl:text-center lt-sm:text-2xl"
          >
            {{ t('collection') }}
            <span class="text-[#FFC658]">{{ t('notifications') }}</span>
          </div>
          <div
            class="max-w-115 mt-10 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] lt-xl:text-center lt-sm:text-lg"
          >
            {{ t('get-hub-notifications') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-[#272727]" />
    </section>

    <section>
      <div
        class="flex flex-row justify-center lt-xl:flex-col-reverse lt-xl:items-center"
      >
        <div class="mt-44.5 mr-42 lt-xl:mx-5.5 lt-xl:mb-16 lt-xl:mt-0">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide lt-xl:text-center lt-sm:text-2xl"
          >
            <span class="text-[#8660C2]">{{ t('voting') }} </span>&nbsp;{{
              t('rights')
            }}
          </div>
          <div
            class="max-w-130.25 mt-11.5 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] lt-xl:text-center lt-sm:text-lg"
          >
            {{ t('as-a-founder-nft') }}
          </div>
        </div>
        <img
          class="w-81.5 h-138 my-5.5 object-cover lt-xl:h-80 lt-xl:object-bottom"
          :alt="t('voting-rights')"
          src="/images/founder/voting-rights.webp"
        />
      </div>
      <div class="w-full h-0.25 bg-[#272727]" />
    </section>
    <section>
      <div class="founder flex justify-center lt-xl:flex-col lt-xl:items-center">
        <NuxtImg
          loading="lazy"
          :alt="t('exclusive-events')"
          class="exclusive-events w-93 h-107.5 object-cover mt-18.5 mb-23 lt-xl:mt-4 lt-xl:mb-0"
          src="/images/founder/exclusive-events.webp"
        />
        <div class="mt-44 ml-47.5 lt-xl:mx-5.5 lt-xl:mb-10.5 lt-xl:mt-4">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide lt-xl:text-center lt-sm:text-2xl"
          >
            <span class="text-[#FFB0F0]">{{ t('exclusive-events') }}</span>
          </div>
          <div
            class="max-w-115 mt-10 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] lt-xl:text-center lt-sm:text-lg"
          >
            {{ t('belong-founder-pass-holders') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-[#272727]" />
    </section>

    <section>
      <div
        class="founder-block flex flex-row justify-center lt-xl:flex-col-reverse lt-xl:items-center lt-xl:mb-7.5 lt-xl:mx-5.5"
      >
        <img
          :alt="t('special') + t('recognition')"
          class="w-139.5 h-140.5 mt-5 z-1 object-cover lt-xl:hidden"
          src="/images/nft/phone-1.webp"
        />
        <img
          src="/images/nft/phone-1-mobile.webp"
          :alt="t('special') + t('recognition')"
          class="max-w-103.5 w-103.5 h-54 mt-5 z-1 object-cover xl:hidden lt-sm:max-w-full lt-sm:w-full lt-sm:h-full"
        />
        <div class="mt-45 lt-xl:mt-6">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wider max-w-135.5 flex flex-row items-center flex-wrap lt-xl:justify-center lt-xl:text-center lt-sm:text-2xl"
          >
            <img
              :alt="t('special') + t('recognition')"
              src="~/assets/images/founder-badge.svg"
              class="mr-3 w-9.5 h-10.75"
            />
            {{ t('special') }}&nbsp;<span class="text-[#8660C2]">{{
              t('recognition')
            }}</span>
          </div>
          <div
            class="text-lg leading-[1.75rem] tracking-wide max-w-115 mt-9.5 text-[#CECECE] ml-12.5 lt-xl:ml-0 lt-xl:text-center lt-sm:text-lg"
          >
            {{ t('nft-holders-get-special') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-[#272727]" />
    </section>

    <section>
      <div
        class="community flex justify-center lt-xl:flex-col-reverse lt-xl:items-center"
      >
        <div class="mt-44 mr-32.5 mb-58.5 lt-xl:flex-col lt-xl:mx-5.5 lt-xl:mb-18 lt-xl:mt-4">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide lt-xl:text-center lt-sm:text-2xl"
          >
            <span class="text-[#A1A1A1]">{{ t('invisibility') }} </span
            >{{ t('mode') }}
          </div>
          <div
            class="max-w-115 mt-10 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] lt-xl:text-center lt-sm:text-lg"
          >
            {{ t('stay-invisible-in-hubs') }}
          </div>
        </div>
        <img
          :alt="t('invisibility') + t('mode')"
          class="w-105.5 h-60.5 mt-40.5 mb-48 lt-xl:mt-4 lt-xl:mb-0 lt-sm:h-full lt-sm:px-3.5"
          src="/images/founder/invisibility-mode.webp"
        />
      </div>
      <div class="w-full h-0.25 bg-[#272727]" />
    </section>
    <section>
      <div class="flex justify-center lt-xl:flex-col lt-xl:items-center">
        <div class="my-19.5 lt-xl:mt-4 lt-xl:mb-0">
          <img
            :alt="t('premium') + t('notifications')"
            class="premium-notifications w-92.5 h-110.75 object-cover mt-4 mb-9 lt-xl:h-74 lt-xl:object-cover lt-xl:object-top lt-xl:mt-0 lt-xl:mb-0"
            src="/images/founder/premium-notifications.webp"
          />
        </div>
        <div class="mt-44.5 ml-36 lt-xl:mx-auto lt-xl:mb-10.5 lt-xl:mt-4">
          <div
            class="flex flex-row items-center text-[1.75rem] leading-[2.625rem] tracking-wide lt-xl:text-center lt-xl:mx-0 lt-xl:justify-center lt-sm:text-2xl lt-sm:px-3.5 lt-sm:flex-wrap"
          >
            <img
              :alt="t('premium') + t('notifications')"
              src="~/assets/images/icon-premium.svg"
              class="w-6 h-3.25"
            />
            <span class="text-[#B18FFF] ml-3">{{ t('premium') }} </span>&nbsp;{{
              t('notifications')
            }}
          </div>
          <div
            class="max-w-115 ml-9 mt-10 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] lt-xl:text-center lt-xl:mx-3.5 lt-sm:text-lg"
          >
            {{ t('subscribe-to-mint-notifications') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-[#272727]" />
    </section>
    <section>
      <YourWebHomepage />
      <div class="w-full h-0.25 bg-[#272727]" />
    </section>
    <section>
      <AGrowthHack />
      <div class="w-full h-0.25 bg-[#272727]" />
    </section>
    <section>
      <Supported class="pt-8 xl:pt-32.5 pb-17" />
    </section>
    <section>
      <InstallSection class="px-5" />
    </section>
  </div>
</template>

<style scoped>
.body {
  font-family: 'Poppins', sans-serif;
}

.header-shadow {
  &:after {
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 118px;
  }
}

.founder-block {
  background: url(~/assets/images/creator/founder-bg.webp);
  background-position: center;
  background-size: cover;
  position: relative;
  &:before {
    background: linear-gradient(180deg, #151515 0%, rgba(21, 21, 21, 0) 100%);
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 172px;
    z-index: 0;
  }
  &:after {
    background: linear-gradient(
      180deg,
      rgba(21, 21, 21, 0) -17.46%,
      #151515 100%
    );
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 185px;
    z-index: 2;
  }
}

.nft-passes {
  position: relative;
  &:before {
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 167px;
    z-index: 2;
  }
}

.gradient {
  background: linear-gradient(360deg, #0069ec 0%, #00b6f6 100%);
}

.phone-5-mobile {
  &:after {
    background: linear-gradient(180deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
    height: 120px;
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 120px;
    z-index: 2;
  }
}

.hub-card {
  &:nth-child(1) {
    margin-top: 0;
  }
  margin-top: -110px;
}

.community {
  position: relative;
  &:after {
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 167px;
  }
}

.soulbound {
  position: relative;
  &:before {
    background: linear-gradient(0, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 167px;
    z-index: 1;
  }
}

.founder {
  position: relative;
  &:after {
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 167px;
  }
}
.secondary-royalties {
  background: url(~/assets/images/creator/secondary-royalties-bg.webp) no-repeat;
  background-position: center;
  background-size: cover;
  position: relative;
  z-index: 2;
  &::before {
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 495px;
    z-index: -1;
  }
}

.web3 {
  &::before {
    background: linear-gradient(180deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 120px;
    z-index: 2;
  }
  &::after {
    background: linear-gradient(0deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 120px;
    z-index: 2;
    left: 0;
    right: 0;
  }
}

.member-profiles {
  &::after {
    background: linear-gradient(0deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 120px;
    z-index: 2;
    left: 0;
    right: 0;
  }
}

.phone-7 {
  position: relative;
  &:before {
    background: linear-gradient(360deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
    height: 120px;
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    z-index: 2;
  }
}

.audited-text {
  @apply absolute left-0 right-0 flex flex-col items-center max-w-163.5 h-64.75 mx-auto top-112.5 lt-xl:px-3.5 lt-xl:top-auto lt-xl:bottom-19.5;
}

@media (max-width: 1279.9px) {
  .nft-passes:before {
    display: none;
  }
  .founder-block,
  .community,
  .founder {
    background: none;
    &:before,
    &:after {
      display: none;
    }
  }
  .header-shadow {
    &:after {
      z-index: -1;
    }
  }
  .soulbound {
    &:before {
      height: 120px !important;
    }
    img {
      object-position: 0 -111px;
    }
    .soulbound-image {
      position: relative;
      &:after {
        background: linear-gradient(
          180deg,
          rgba(34, 34, 34, 0) 0%,
          #151515 100%
        );
        content: '';
        position: absolute;
        bottom: 0;
        width: 100%;
        height: 120px;
        z-index: 1;
      }
    }
  }
}

@media (max-width: 424.9px) {
  .wl-sports,
  .collection-notifications,
  .exclusive-events,
  .premium-notifications {
    @apply w-full h-full px-3.5;
  }
}
</style>
